import {useMemo} from 'react';
import type {MessageDescriptor} from 'react-intl';
import {defineMessages, useIntl} from 'react-intl';
import {defineAccessControlledResource, GroupAccessRule} from '@igp/shared/access-control';
import type {Sorting} from '@igp/shared/api';
import {useAuthUser} from '@igp/shared/auth';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import type {ChipValue} from '@igp/shared/components/chip';
import type {ChipColumn, CurrencyColumn, DataGridColumn, TextColumn} from '@igp/shared/components/data-grid';
import {AgentPlayerReportingV2} from '@igp/shared/contract/pam';
import type {UseDownloadProps, UseDownloadResult} from '@igp/shared/redux/download';
import {useDownload} from '@igp/shared/redux/download';
import type {RawItem, UseListViewEntityProps, UseListViewEntityResult, ViewType} from '@igp/shared/redux/view';
import {extendedViewCleanDelay} from '@igp/shared/redux/view';
import {useColumnsSettings} from '@igp/shared/user-settings';
import {momentToTimestampSeconds} from '@igp/shared/utils';
import {EntityType} from '@models';
import {useViewInit} from '@redux';

import {groups, useBoAgentAccessCheck} from '@access-control';

import {
    PlayerLevelReportV2ColumnSettingKeysTypeName,
    PlayerLevelReportV2QueryItemsKeysTypeName,
    PlayerLevelReportV2QuerySummaryKeysTypeName,
    PlayerLevelReportV2SortKeysTypeName,
    PlayerLevelReportV2ViewModelKeysTypeName,
} from './constants';
import {firstAvailableDate} from './constants';
import {
    type IsPro,
    type PlayerLevelReportV2ColumnSetting,
    type PlayerLevelReportV2ColumnSettingKeys,
    type PlayerLevelReportV2QueryFilterKeys,
    type PlayerLevelReportV2QueryKeys,
    type PlayerLevelReportV2SortKeys,
    PlayerLevelReportV2ViewModel,
    type PlayerLevelReportV2ViewModelKeys,
    ServiceTypes,
} from './types';

const localized = defineMessages({
    total: {
        id: 'playerLevelReportHooks_total',
        defaultMessage: 'Total',
    },
    pro: {
        id: 'playerLevelReportHooks_pro',
        defaultMessage: 'Pro',
    },
    nonPro: {
        id: 'playerLevelReportHooks_nonPro',
        defaultMessage: 'Non-Pro',
    },
    mixed: {
        id: 'playerLevelReportHooks_mixed',
        defaultMessage: 'Mixed',
    },
    ['bonus_cost_summary.cash_realized_bonus']: {
        id: 'PlayerLevelReportColumn_bonus_cost_summary.cash_realized_bonus',
        defaultMessage: 'Cash Realized Bonus',
    },
    ['bonus_cost_summary.cost_ftd_bonus_pro']: {
        id: 'PlayerLevelReportColumn_bonus_cost_summary.cost_ftd_bonus_pro',
        defaultMessage: 'FTD Bonus Pro',
    },
    ['bonus_cost_summary.cost_psp_fee_pro']: {
        id: 'PlayerLevelReportColumn_bonus_cost_summary.cost_psp_fee_pro',
        defaultMessage: 'Cost PSP Fee Pro',
    },
    ['bonus_cost_summary.cost_tournament_overlay']: {
        id: 'PlayerLevelReportColumn_bonus_cost_summary.cost_tournament_overlay',
        defaultMessage: 'Cost Tournament Overlay',
    },
    ['bonus_cost_summary.cost_tournament_overlay_excl_freeroll']: {
        id: 'PlayerLevelReportColumn_bonus_cost_summary.cost_tournament_overlay_excl_freeroll',
        defaultMessage: 'Cost Tournament Overlay Excl Freeroll',
    },
    ['bonus_cost_summary.mtt_ticket_bonus']: {
        id: 'PlayerLevelReportColumn_bonus_cost_summary.mtt_ticket_bonus',
        defaultMessage: 'MTT Ticket Bonus',
    },
    ['bonus_cost_summary.poker_bonus']: {
        id: 'PlayerLevelReportColumn_bonus_cost_summary.poker_bonus',
        defaultMessage: 'Poker Bonus',
    },
    ['bonus_cost_summary.psp_fee']: {
        id: 'PlayerLevelReportColumn_bonus_cost_summary.psp_fee',
        defaultMessage: 'PSP Fee',
    },
    ['casino_summary.casino_bonus']: {
        id: 'PlayerLevelReportColumn_casino_summary.casino_bonus',
        defaultMessage: 'Casino Bonus',
    },
    ['casino_summary.casino_ggr']: {
        id: 'PlayerLevelReportColumn_casino_summary.casino_ggr',
        defaultMessage: 'Casino GGR',
    },
    ['casino_summary.casino_ngr']: {
        id: 'PlayerLevelReportColumn_casino_summary.casino_ngr',
        defaultMessage: 'Casino NGR',
    },
    ['casino_summary.casino_share']: {
        id: 'PlayerLevelReportColumn_casino_summary.casino_share',
        defaultMessage: 'Casino Share',
    },
    ['casino_summary.external_casino_ggr']: {
        id: 'PlayerLevelReportColumn_casino_summary.external_casino_ggr',
        defaultMessage: 'External Casino GGR',
    },
    ['casino_summary.internal_casino_ggr']: {
        id: 'PlayerLevelReportColumn_casino_summary.internal_casino_ggr',
        defaultMessage: 'Internal Casino GGR',
    },
    ['casino_summary.casino_wager']: {
        id: 'PlayerLevelReportColumn_casino_summary.casino_wager',
        defaultMessage: 'Casino Wager',
    },
    ['global_spins_summary.cost_global_spins_tickets']: {
        id: 'PlayerLevelReportColumn_global_spins_summary.cost_global_spins_tickets',
        defaultMessage: 'Cost Spins Tournament Tickets',
    },
    ['global_spins_summary.global_spins_expected_ggr']: {
        id: 'PlayerLevelReportColumn_global_spins_summary.global_spins_expected_ggr',
        defaultMessage: 'Global Spins EGGR',
    },
    ['global_spins_summary.global_spins_ggr']: {
        id: 'PlayerLevelReportColumn_global_spins_summary.global_spins_ggr',
        defaultMessage: 'Global Spins GGR',
    },
    ['player_activity_summary.cash_game_hands_played']: {
        id: 'PlayerLevelReportColumn_player_activity_summary.cash_game_hands_played',
        defaultMessage: 'Cash game hand played',
    },
    ['player_activity_summary.ecology_flag']: {
        id: 'PlayerLevelReportColumn_player_activity_summary.ecology_flag',
        defaultMessage: 'Ecology Flag',
    },
    ['player_activity_summary.mtt_played']: {
        id: 'PlayerLevelReportColumn_player_activity_summary.mtt_played',
        defaultMessage: 'MTT played',
    },
    ['player_activity_summary.player_profit_loss']: {
        id: 'PlayerLevelReportColumn_player_activity_summary.player_profit_loss',
        defaultMessage: 'Profit Loss',
    },
    ['player_activity_summary.player_win_loss']: {
        id: 'PlayerLevelReportColumn_player_activity_summary.player_win_loss',
        defaultMessage: 'Player Win/Loss',
    },
    ['player_activity_summary.net_deposit']: {
        id: 'PlayerLevelReportColumn_player_activity_summary.net_deposit',
        defaultMessage: 'Player Net Deposit',
    },
    ['poker_mtt_summary.mtt_ggr']: {
        id: 'PlayerLevelReportColumn_poker_mtt_summary.mtt_ggr',
        defaultMessage: 'MTT GGR',
    },
    ['poker_mtt_summary.mtt_ngr']: {
        id: 'PlayerLevelReportColumn_poker_mtt_summary.mtt_ngr',
        defaultMessage: 'MTT NGR',
    },
    ['poker_mtt_summary.mtt_ticket_bonus']: {
        id: 'PlayerLevelReportColumn_poker_mtt_summary.mtt_ticket_bonus',
        defaultMessage: 'MTT Ticket Bonus',
    },
    ['poker_mtt_summary.mtt_total_buyin']: {
        id: 'PlayerLevelReportColumn_poker_mtt_summary.mtt_total_buyin',
        defaultMessage: 'MTT Total Buyin',
    },
    ['poker_mtt_summary.player_mtt_win_loss']: {
        id: 'PlayerLevelReportColumn_poker_mtt_summary.player_mtt_win_loss',
        defaultMessage: 'MTT Win/Loss',
    },
    ['poker_mtt_summary.poker_mtt_fees']: {
        id: 'PlayerLevelReportColumn_poker_mtt_summary.poker_mtt_fees',
        defaultMessage: 'Poker MTT Fees',
    },
    ['poker_nlhe_summary.is_pro']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.is_pro',
        defaultMessage: 'Is Pro NLHE',
    },
    ['poker_nlhe_summary.is_pro_shortdeck']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.is_pro_shortdeck',
        defaultMessage: 'Is Pro Shortdeck',
    },
    ['poker_nlhe_summary.nlhe_add_on_ggr_usd_pro']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.nlhe_add_on_ggr_usd_pro',
        defaultMessage: 'NLHE Add On GGR USD Pro',
    },
    ['poker_nlhe_summary.nlhe_ggr_usd_pro']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.nlhe_ggr_usd_pro',
        defaultMessage: 'NLHE GGR USD Pro',
    },
    ['poker_nlhe_summary.nlhe_insurance_ggr_usd_pro']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.nlhe_insurance_ggr_usd_pro',
        defaultMessage: 'NLHE Insurance GGR USD Pro',
    },
    ['poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro',
        defaultMessage: 'NLHE Jackpot GGR USD Pro',
    },
    ['poker_nlhe_summary.nlhe_rakes_ggr_usd_pro']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.nlhe_rakes_ggr_usd_pro',
        defaultMessage: 'NLHE Rakes GGR USD Pro',
    },
    ['poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime',
        defaultMessage: 'Poker Win/Loss NLHE/Shortdeck Lifetime',
    },
    ['poker_nlhe_summary.ggr_poker_prev_180_days']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.ggr_poker_prev_180_days',
        defaultMessage: 'Poker GGR NHLE/Shortdeck Prev 180 Days',
    },
    ['poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days',
        defaultMessage: 'Poker Win/Loss NLHE/Shortdeck Prev 180 Days',
    },
    ['poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro',
        defaultMessage: 'Shortdeck Add On GGR USD Pro',
    },
    ['poker_nlhe_summary.shortdeck_ggr_usd_pro']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.shortdeck_ggr_usd_pro',
        defaultMessage: 'Shortdeck GGR USD Pro',
    },
    ['poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro',
        defaultMessage: 'Shortdeck Insurance GGR USD Pro',
    },
    ['poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro',
        defaultMessage: 'Shortdeck Rakes GGR USD Pro',
    },
    ['poker_nlhe_summary.nlhe_micro_ggr_usd_pro']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.nlhe_micro_ggr_usd_pro',
        defaultMessage: 'NLHE Micro GGR (USD, Pro)',
    },
    ['poker_nlhe_summary.nlhe_low_ggr_usd_pro']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.nlhe_low_ggr_usd_pro',
        defaultMessage: 'NLHE Low GGR (USD, Pro)',
    },
    ['poker_nlhe_summary.nlhe_medium_ggr_usd_pro']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.nlhe_medium_ggr_usd_pro',
        defaultMessage: 'NLHE Medium GGR (USD, Pro)',
    },
    ['poker_nlhe_summary.nlhe_high_ggr_usd_pro']: {
        id: 'PlayerLevelReportColumn_poker_nlhe_summary.nlhe_high_ggr_usd_pro',
        defaultMessage: 'NLHE High GGR (USD, Pro)',
    },
    ['poker_plo_summary.plo_ggr']: {
        id: 'PlayerLevelReportColumn_poker_plo_summary.plo_ggr',
        defaultMessage: 'PLO GGR',
    },
    ['poker_summary.poker_addons']: {
        id: 'PlayerLevelReportColumn_poker_summary.poker_addons',
        defaultMessage: 'Poker Addons Total',
    },
    ['poker_summary.poker_addons_add_on']: {
        id: 'PlayerLevelReportColumn_poker_summary.poker_addons_add_on',
        defaultMessage: 'Poker Addons Features',
    },
    ['poker_summary.poker_addons_guess']: {
        id: 'PlayerLevelReportColumn_poker_summary.poker_addons_guess',
        defaultMessage: 'Poker Addons Guess',
    },
    ['poker_summary.poker_addons_insurance']: {
        id: 'PlayerLevelReportColumn_poker_summary.poker_addons_insurance',
        defaultMessage: 'Poker Addons Insurance',
    },
    ['poker_summary.poker_ggr']: {
        id: 'PlayerLevelReportColumn_poker_summary.poker_ggr',
        defaultMessage: 'Poker GGR',
    },
    ['poker_summary.poker_ggr_usd_pro_ecology_modified']: {
        id: 'PlayerLevelReportColumn_poker_summary.poker_ggr_usd_pro_ecology_modified',
        defaultMessage: 'Poker GGR USD Pro Ecology Modified',
    },
    ['poker_summary.poker_ngr']: {
        id: 'PlayerLevelReportColumn_poker_summary.poker_ngr',
        defaultMessage: 'Poker NGR',
    },
    ['poker_summary.poker_rake']: {
        id: 'PlayerLevelReportColumn_poker_summary.poker_rake',
        defaultMessage: 'Poker Rake',
    },
    ['poker_summary.poker_share']: {
        id: 'PlayerLevelReportColumn_poker_summary.poker_share',
        defaultMessage: 'Poker Share',
    },
    ['share_summary.casino_share']: {
        id: 'PlayerLevelReportColumn_share_summary.casino_share',
        defaultMessage: 'Casino Share',
    },
    ['share_summary.global_spins_expected_ggr']: {
        id: 'PlayerLevelReportColumn_share_summary.global_spins_expected_ggr',
        defaultMessage: 'Global Spins EGGR',
    },
    ['share_summary.mtt_ggr']: {
        id: 'PlayerLevelReportColumn_share_summary.mtt_ggr',
        defaultMessage: 'MTT GGR',
    },
    ['share_summary.plo_ggr']: {
        id: 'PlayerLevelReportColumn_share_summary.plo_ggr',
        defaultMessage: 'PLO GGR',
    },
    ['share_summary.poker_share']: {
        id: 'PlayerLevelReportColumn_share_summary.poker_share',
        defaultMessage: 'Poker Share',
    },
    ['share_summary.sports_settled_bet']: {
        id: 'PlayerLevelReportColumn_share_summary.sports_settled_bet',
        defaultMessage: 'Sports Settled Bet',
    },
    ['sports_summary.sports_ggr']: {
        id: 'PlayerLevelReportColumn_sports_summary.sports_ggr',
        defaultMessage: 'Sports GGR',
    },
    ['sports_summary.sports_settled_bet']: {
        id: 'PlayerLevelReportColumn_sports_summary.sports_settled_bet',
        defaultMessage: 'Sports Settled Bet',
    },
    iso_alpha2_country_code: {
        id: 'PlayerLevelReportColumn_iso_alpha2_country_code',
        defaultMessage: 'Country',
    },
    path: {
        id: 'PlayerLevelReportColumn_path',
        defaultMessage: '',
    },
    referrer_agent_id: {
        id: 'PlayerLevelReportColumn_referrer_agent_id',
        defaultMessage: 'Agent ID',
    },
    referrer_agent_username: {
        id: 'PlayerLevelReportColumn_referrer_agent_username',
        defaultMessage: 'Agent Username',
    },
    uid: {
        id: 'PlayerLevelReportColumn_uid',
        defaultMessage: 'Player ID',
    },
    username: {
        id: 'PlayerLevelReportColumn_username',
        defaultMessage: 'Player Username',
    },
    'player_ecology.ecology_flag': {
        id: 'PlayerLevelReportColumn_player_ecology.ecology_flag',
        defaultMessage: 'Ecology Flag',
    },
    'player_ecology.is_pro_shortdeck': {
        id: 'PlayerLevelReportColumn_player_ecology.is_pro_shortdeck',
        defaultMessage: 'Shortdeck PRO',
    },
    'player_ecology.is_pro_zoom': {
        id: 'PlayerLevelReportColumn_player_ecology.is_pro_zoom',
        defaultMessage: 'Zoom PRO',
    },
    'player_ecology.stake_2_is_pro_nlhe': {
        id: 'PlayerLevelReportColumn_player_ecology.stake_2_is_pro_nlhe',
        defaultMessage: 'NLHE $2 PRO',
    },
    'player_ecology.stake_4_is_pro_nlhe': {
        id: 'PlayerLevelReportColumn_player_ecology.stake_4_is_pro_nlhe',
        defaultMessage: 'NLHE $4 PRO',
    },
    'player_ecology.stake_10_is_pro_nlhe': {
        id: 'PlayerLevelReportColumn_player_ecology.stake_10_is_pro_nlhe',
        defaultMessage: 'NLHE $10 PRO',
    },
    'player_ecology.stake_20_is_pro_nlhe': {
        id: 'PlayerLevelReportColumn_player_ecology.stake_20_is_pro_nlhe',
        defaultMessage: 'NLHE $20 PRO',
    },
    'player_ecology.stake_50_is_pro_nlhe': {
        id: 'PlayerLevelReportColumn_player_ecology.stake_50_is_pro_nlhe',
        defaultMessage: 'NLHE $50 PRO',
    },
    'player_ecology.stake_100_is_pro_nlhe': {
        id: 'PlayerLevelReportColumn_player_ecology.stake_100_is_pro_nlhe',
        defaultMessage: 'NLHE $100 PRO',
    },
    'player_ecology.stake_200_is_pro_nlhe': {
        id: 'PlayerLevelReportColumn_player_ecology.stake_200_is_pro_nlhe',
        defaultMessage: 'NLHE $200 PRO',
    },
    'player_ecology.stake_400_is_pro_nlhe': {
        id: 'PlayerLevelReportColumn_player_ecology.stake_400_is_pro_nlhe',
        defaultMessage: 'NLHE $400 PRO',
    },
    'total_ggr_summary.ggr': {
        id: 'PlayerLevelReportColumn_ggr',
        defaultMessage: 'Total GGR',
    },
});

type UsePlayerLevelReportV2Props = Omit<
    UseListViewEntityProps<PlayerLevelReportV2QueryFilterKeys, PlayerLevelReportV2ViewModelKeys>,
    'schemaMapper' | 'isCancelable' | 'realtimeMode' | 'triggers' | 'ids'
> & {
    hasSummary?: boolean;
};

function useLocalizedHeaders(): Record<PlayerLevelReportV2ViewModelKeys, MessageDescriptor> {
    const hasAccessToRowColumns = useBoAgentAccessCheck(rowColumnsResource);

    return useMemo(
        () => ({
            ['bonus_cost_summary.cash_realized_bonus']: localized['bonus_cost_summary.cash_realized_bonus'],
            ['bonus_cost_summary.cost_ftd_bonus_pro']: localized['bonus_cost_summary.cost_ftd_bonus_pro'],
            ['bonus_cost_summary.cost_psp_fee_pro']: localized['bonus_cost_summary.cost_psp_fee_pro'],
            ['bonus_cost_summary.cost_tournament_overlay']: localized['bonus_cost_summary.cost_tournament_overlay'],
            ['bonus_cost_summary.cost_tournament_overlay_excl_freeroll']:
                localized['bonus_cost_summary.cost_tournament_overlay_excl_freeroll'],
            ['bonus_cost_summary.mtt_ticket_bonus']: localized['bonus_cost_summary.mtt_ticket_bonus'],
            ['bonus_cost_summary.poker_bonus']: localized['bonus_cost_summary.poker_bonus'],
            ['bonus_cost_summary.psp_fee']: localized['bonus_cost_summary.psp_fee'],
            ['casino_summary.casino_bonus']: localized['casino_summary.casino_bonus'],
            ['casino_summary.casino_ggr']: localized['casino_summary.casino_ggr'],
            ['casino_summary.casino_ngr']: localized['casino_summary.casino_ngr'],
            ['casino_summary.casino_share']: localized['casino_summary.casino_share'],
            ['casino_summary.external_casino_ggr']: localized['casino_summary.external_casino_ggr'],
            ['casino_summary.internal_casino_ggr']: localized['casino_summary.internal_casino_ggr'],
            ['casino_summary.casino_wager']: localized['casino_summary.casino_wager'],
            ['global_spins_summary.cost_global_spins_tickets']: localized['global_spins_summary.cost_global_spins_tickets'],
            ['global_spins_summary.global_spins_expected_ggr']: localized['global_spins_summary.global_spins_expected_ggr'],
            ['global_spins_summary.global_spins_ggr']: localized['global_spins_summary.global_spins_ggr'],
            ['player_activity_summary.cash_game_hands_played']: localized['player_activity_summary.cash_game_hands_played'],
            ['player_activity_summary.ecology_flag']: localized['player_activity_summary.ecology_flag'],
            ['player_activity_summary.mtt_played']: localized['player_activity_summary.mtt_played'],
            ['player_activity_summary.player_profit_loss']: localized['player_activity_summary.player_profit_loss'],
            ['player_activity_summary.player_win_loss']: localized['player_activity_summary.player_win_loss'],
            ['player_activity_summary.net_deposit']: localized['player_activity_summary.net_deposit'],
            ['player_ecology.ecology_flag']: localized['player_ecology.ecology_flag'],
            ['player_ecology.is_pro_shortdeck']: localized['player_ecology.is_pro_shortdeck'],
            ['player_ecology.is_pro_zoom']: localized['player_ecology.is_pro_zoom'],
            ['player_ecology.stake_2_is_pro_nlhe']: localized['player_ecology.stake_2_is_pro_nlhe'],
            ['player_ecology.stake_4_is_pro_nlhe']: localized['player_ecology.stake_4_is_pro_nlhe'],
            ['player_ecology.stake_10_is_pro_nlhe']: localized['player_ecology.stake_10_is_pro_nlhe'],
            ['player_ecology.stake_20_is_pro_nlhe']: localized['player_ecology.stake_20_is_pro_nlhe'],
            ['player_ecology.stake_50_is_pro_nlhe']: localized['player_ecology.stake_50_is_pro_nlhe'],
            ['player_ecology.stake_100_is_pro_nlhe']: localized['player_ecology.stake_100_is_pro_nlhe'],
            ['player_ecology.stake_200_is_pro_nlhe']: localized['player_ecology.stake_200_is_pro_nlhe'],
            ['player_ecology.stake_400_is_pro_nlhe']: localized['player_ecology.stake_400_is_pro_nlhe'],
            ['poker_mtt_summary.mtt_ggr']: localized['poker_mtt_summary.mtt_ggr'],
            ['poker_mtt_summary.mtt_ngr']: localized['poker_mtt_summary.mtt_ngr'],
            ['poker_mtt_summary.mtt_ticket_bonus']: localized['poker_mtt_summary.mtt_ticket_bonus'],
            ['poker_mtt_summary.mtt_total_buyin']: localized['poker_mtt_summary.mtt_total_buyin'],
            ['poker_mtt_summary.player_mtt_win_loss']: localized['poker_mtt_summary.player_mtt_win_loss'],
            ['poker_mtt_summary.poker_mtt_fees']: localized['poker_mtt_summary.poker_mtt_fees'],
            ['poker_nlhe_summary.is_pro']: localized['poker_nlhe_summary.is_pro'],
            ['poker_nlhe_summary.is_pro_shortdeck']: localized['poker_nlhe_summary.is_pro_shortdeck'],
            ['poker_nlhe_summary.nlhe_add_on_ggr_usd_pro']: localized['poker_nlhe_summary.nlhe_add_on_ggr_usd_pro'],
            ['poker_nlhe_summary.nlhe_ggr_usd_pro']: localized['poker_nlhe_summary.nlhe_ggr_usd_pro'],
            ['poker_nlhe_summary.nlhe_insurance_ggr_usd_pro']: localized['poker_nlhe_summary.nlhe_insurance_ggr_usd_pro'],
            ['poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro']: localized['poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro'],
            ['poker_nlhe_summary.nlhe_rakes_ggr_usd_pro']: localized['poker_nlhe_summary.nlhe_rakes_ggr_usd_pro'],
            ['poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime']:
                localized['poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime'],
            ['poker_nlhe_summary.ggr_poker_prev_180_days']: localized['poker_nlhe_summary.ggr_poker_prev_180_days'],
            ['poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days']:
                localized['poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days'],
            ['poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro']: localized['poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro'],
            ['poker_nlhe_summary.shortdeck_ggr_usd_pro']: localized['poker_nlhe_summary.shortdeck_ggr_usd_pro'],
            ['poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro']: localized['poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro'],
            ['poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro']: localized['poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro'],
            ['poker_nlhe_summary.nlhe_micro_ggr_usd_pro']: localized['poker_nlhe_summary.nlhe_micro_ggr_usd_pro'],
            ['poker_nlhe_summary.nlhe_low_ggr_usd_pro']: localized['poker_nlhe_summary.nlhe_low_ggr_usd_pro'],
            ['poker_nlhe_summary.nlhe_medium_ggr_usd_pro']: localized['poker_nlhe_summary.nlhe_medium_ggr_usd_pro'],
            ['poker_nlhe_summary.nlhe_high_ggr_usd_pro']: localized['poker_nlhe_summary.nlhe_high_ggr_usd_pro'],
            ['poker_plo_summary.plo_ggr']: localized['poker_plo_summary.plo_ggr'],
            ['poker_summary.poker_addons']: localized['poker_summary.poker_addons'],
            ['poker_summary.poker_addons_add_on']: localized['poker_summary.poker_addons_add_on'],
            ['poker_summary.poker_addons_guess']: localized['poker_summary.poker_addons_guess'],
            ['poker_summary.poker_addons_insurance']: localized['poker_summary.poker_addons_insurance'],
            ['poker_summary.poker_ggr']: localized['poker_summary.poker_ggr'],
            ['poker_summary.poker_ggr_usd_pro_ecology_modified']: localized['poker_summary.poker_ggr_usd_pro_ecology_modified'],
            ['poker_summary.poker_ngr']: localized['poker_summary.poker_ngr'],
            ['poker_summary.poker_rake']: localized['poker_summary.poker_rake'],
            ['poker_summary.poker_share']: hasAccessToRowColumns
                ? localized['poker_summary.poker_share']
                : localized['poker_summary.poker_ggr'],
            ['share_summary.casino_share']: localized['share_summary.casino_share'],
            ['share_summary.global_spins_expected_ggr']: localized['share_summary.global_spins_expected_ggr'],
            ['share_summary.mtt_ggr']: localized['share_summary.mtt_ggr'],
            ['share_summary.plo_ggr']: localized['share_summary.plo_ggr'],
            ['share_summary.poker_share']: localized['share_summary.poker_share'],
            ['share_summary.sports_settled_bet']: localized['share_summary.sports_settled_bet'],
            ['sports_summary.sports_ggr']: localized['sports_summary.sports_ggr'],
            ['sports_summary.sports_settled_bet']: localized['sports_summary.sports_settled_bet'],
            iso_alpha2_country_code: localized['iso_alpha2_country_code'],
            path: localized['path'],
            referrer_agent_id: localized['referrer_agent_id'],
            referrer_agent_username: localized['referrer_agent_username'],
            uid: localized['uid'],
            username: localized['username'],
            'total_ggr_summary.ggr': localized['total_ggr_summary.ggr'],
        }),
        [hasAccessToRowColumns]
    );
}

type UsePlayerLevelReportV2Result = UseListViewEntityResult<PlayerLevelReportV2ViewModel, PlayerLevelReportV2ViewModelKeys> & {
    summary: PlayerLevelReportV2ViewModel;
    summaryRaw: RawItem;
};

export function usePlayerLevelReportV2({
    viewType,
    fields,
    defaultFilters,
    defaultSorting,
    defaultPaging,
    syncWithUrl,
    validateFilter,
    hasSummary,
    cleanDelay = extendedViewCleanDelay,
    displayName,
}: UsePlayerLevelReportV2Props): UsePlayerLevelReportV2Result {
    const mapper = useAutoMapper();
    const queryFields = useMemo(() => getQueryFields(fields), [fields]);
    const mappedSorting = useMemo(() => mapSorting(defaultSorting), [defaultSorting]);

    const {
        items: serverItems,
        totalCount,
        viewEntity,
        getSearchFilter,
        handleSortChange,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
    } = useViewInit<AgentPlayerReportingV2, PlayerLevelReportV2QueryFilterKeys, PlayerLevelReportV2QueryKeys, PlayerLevelReportV2SortKeys>({
        viewType,
        entity: {entity: EntityType.PlayerLevelReportV2, fields: queryFields},
        cleanDelay,
        defaultFilters,
        hiddenFilters: [{key: 'started_at_min', value: momentToTimestampSeconds(firstAvailableDate)}],
        defaultPaging,
        defaultSorting: mappedSorting,
        syncWithUrl,
        validateFilter,
        displayName,
    });

    const items = useMemo(() => {
        return serverItems?.map(i =>
            mapper.tryMap<AgentPlayerReportingV2, PlayerLevelReportV2ViewModel>(i, AgentPlayerReportingV2, PlayerLevelReportV2ViewModel)
        );
    }, [serverItems]);

    const searchFilter = useMemo(() => {
        return getSearchFilter(i =>
            mapper.map<PlayerLevelReportV2SortKeys, PlayerLevelReportV2ViewModelKeys>(
                i,
                PlayerLevelReportV2SortKeysTypeName,
                PlayerLevelReportV2ViewModelKeysTypeName
            )
        );
    }, [getSearchFilter]);

    function getQueryFields(vmKeys: PlayerLevelReportV2ViewModelKeys[]) {
        const queryItemsFields =
            vmKeys.map(f =>
                mapper.map<PlayerLevelReportV2ViewModelKeys, PlayerLevelReportV2QueryKeys>(
                    f,
                    PlayerLevelReportV2ViewModelKeysTypeName,
                    PlayerLevelReportV2QueryItemsKeysTypeName
                )
            ) ?? [];
        const querySummaryFields = hasSummary
            ? vmKeys.map(f =>
                  mapper.map<PlayerLevelReportV2ViewModelKeys, PlayerLevelReportV2QueryKeys>(
                      f,
                      PlayerLevelReportV2ViewModelKeysTypeName,
                      PlayerLevelReportV2QuerySummaryKeysTypeName
                  )
              )
            : [];

        return [...queryItemsFields, ...querySummaryFields].filter(f => f);
    }

    function mapSorting(sorting: Sorting<PlayerLevelReportV2ViewModelKeys>[]): Sorting<PlayerLevelReportV2SortKeys>[] {
        return (
            sorting?.map(i => ({
                field: mapper.map<PlayerLevelReportV2ViewModelKeys, PlayerLevelReportV2SortKeys>(
                    i.field,
                    PlayerLevelReportV2ViewModelKeysTypeName,
                    PlayerLevelReportV2SortKeysTypeName
                ),
                sort: i.sort,
            })) ?? []
        );
    }

    function handleVmSortChange(sorting: Sorting<PlayerLevelReportV2ViewModelKeys>[]): void {
        handleSortChange(mapSorting(sorting));
    }

    const reportItems = items?.filter(i => !i.model?.isSummary);
    const summary = items?.find(i => i.model?.isSummary);

    return {
        items: reportItems?.map(i => i.model),
        rawItems: reportItems?.map(i => ({isValid: i.isModelValid, raw: i.rawModelString})),
        summary: summary?.model,
        summaryRaw: {isValid: summary?.isModelValid, raw: summary?.rawModelString},
        totalCount,
        searchFilter,
        filterString: viewEntity?.filter,
        handleSortChange: handleVmSortChange,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
    };
}

export function useDataGridPlayerLevelReportV2Columns(
    keys: PlayerLevelReportV2ViewModelKeys[]
): DataGridColumn<PlayerLevelReportV2ViewModel>[] {
    const {formatMessage} = useIntl();
    const localizedHeaders = useLocalizedHeaders();

    const defaultColumn: DataGridColumn<PlayerLevelReportV2ViewModel> = {
        field: undefined,
        type: undefined,
        valueGetter: undefined,
        width: 150,
    };
    const currency = 'USD';

    const bonusCostSummaryCashRealizedBonus: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.bonus_cost_summary?.cash_realized_bonus, currency}),
        field: 'bonus_cost_summary.cash_realized_bonus',
        title: localizedHeaders['bonus_cost_summary.cash_realized_bonus'],
        sortable: true,
    };
    const bonusCostSummaryCostFtdBonusPro: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.bonus_cost_summary?.cost_ftd_bonus_pro, currency}),
        field: 'bonus_cost_summary.cost_ftd_bonus_pro',
        title: localizedHeaders['bonus_cost_summary.cost_ftd_bonus_pro'],
        sortable: true,
    };
    const bonusCostSummaryCostPspFeePro: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.bonus_cost_summary?.cost_psp_fee_pro, currency}),
        field: 'bonus_cost_summary.cost_psp_fee_pro',
        title: localizedHeaders['bonus_cost_summary.cost_psp_fee_pro'],
        sortable: true,
    };
    const bonusCostSummaryCostTournamentOverlay: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.bonus_cost_summary?.cost_tournament_overlay, currency}),
        field: 'bonus_cost_summary.cost_tournament_overlay',
        title: localizedHeaders['bonus_cost_summary.cost_tournament_overlay'],
        sortable: true,
    };
    const bonusCostSummaryCostTournamentOverlayExclFreeroll: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.bonus_cost_summary?.cost_tournament_overlay_excl_freeroll, currency}),
        field: 'bonus_cost_summary.cost_tournament_overlay_excl_freeroll',
        title: localizedHeaders['bonus_cost_summary.cost_tournament_overlay_excl_freeroll'],
        sortable: true,
    };
    const bonusCostSummaryMttTicketBonus: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.bonus_cost_summary?.mtt_ticket_bonus, currency}),
        field: 'bonus_cost_summary.mtt_ticket_bonus',
        title: localizedHeaders['bonus_cost_summary.mtt_ticket_bonus'],
        sortable: true,
    };
    const bonusCostSummaryPokerBonus: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.bonus_cost_summary?.poker_bonus, currency}),
        field: 'bonus_cost_summary.poker_bonus',
        title: localizedHeaders['bonus_cost_summary.poker_bonus'],
        sortable: true,
    };
    const bonusCostSummaryPspFee: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.bonus_cost_summary?.psp_fee, currency}),
        field: 'bonus_cost_summary.psp_fee',
        title: localizedHeaders['bonus_cost_summary.psp_fee'],
        sortable: true,
    };
    const casinoSummaryCasinoBonus: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.casino_summary?.casino_bonus, currency}),
        field: 'casino_summary.casino_bonus',
        title: localizedHeaders['casino_summary.casino_bonus'],
        sortable: true,
    };
    const casinoSummaryCasinoGgr: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.casino_summary?.casino_ggr, currency}),
        field: 'casino_summary.casino_ggr',
        title: localizedHeaders['casino_summary.casino_ggr'],
        sortable: true,
    };
    const casinoSummaryCasinoNgr: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.casino_summary?.casino_ngr, currency}),
        field: 'casino_summary.casino_ngr',
        title: localizedHeaders['casino_summary.casino_ngr'],
        sortable: true,
    };
    const casinoSummaryCasinoShare: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.casino_summary?.casino_share, currency}),
        field: 'casino_summary.casino_share',
        title: localizedHeaders['casino_summary.casino_share'],
        sortable: true,
    };
    const casinoSummaryExternalCasinoGgr: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.casino_summary?.external_casino_ggr, currency}),
        field: 'casino_summary.external_casino_ggr',
        title: localizedHeaders['casino_summary.external_casino_ggr'],
        sortable: true,
    };
    const casinoSummaryInternalCasinoGgr: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.casino_summary?.internal_casino_ggr, currency}),
        field: 'casino_summary.internal_casino_ggr',
        title: localizedHeaders['casino_summary.internal_casino_ggr'],
        sortable: true,
    };
    const casinoSummaryCasinoWager: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.casino_summary?.casino_wager, currency}),
        field: 'casino_summary.casino_wager',
        title: localizedHeaders['casino_summary.casino_wager'],
        sortable: true,
    };
    const globalSpinsSummaryCostGlobalSpinsTickets: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.global_spins_summary?.cost_global_spins_tickets, currency}),
        field: 'global_spins_summary.cost_global_spins_tickets',
        title: localizedHeaders['global_spins_summary.cost_global_spins_tickets'],
        sortable: true,
    };
    const globalSpinsSummaryGlobalSpinsExpectedGgr: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.global_spins_summary?.global_spins_expected_ggr, currency}),
        field: 'global_spins_summary.global_spins_expected_ggr',
        title: localizedHeaders['global_spins_summary.global_spins_expected_ggr'],
        sortable: true,
    };
    const globalSpinsSummaryGlobalSpinsGgr: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...globalSpinsSummaryGlobalSpinsExpectedGgr,
        field: 'global_spins_summary.global_spins_ggr',
        title: localizedHeaders['global_spins_summary.global_spins_ggr'],
    };
    const playerActivitySummaryCashGameHandsPlayed: TextColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'text',
        valueGetter: m => m?.player_activity_summary?.cash_game_hands_played?.toString(),
        field: 'player_activity_summary.cash_game_hands_played',
        title: localizedHeaders['player_activity_summary.cash_game_hands_played'],
        sortable: true,
    };
    const playerActivitySummaryEcologyFlag: TextColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'text',
        valueGetter: m => m?.player_activity_summary?.ecology_flag,
        field: 'player_activity_summary.ecology_flag',
        title: localizedHeaders['player_activity_summary.ecology_flag'],
        sortable: true,
    };
    const playerActivitySummaryMttPlayed: TextColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'text',
        valueGetter: m => m?.player_activity_summary?.mtt_played?.toString(),
        field: 'player_activity_summary.mtt_played',
        title: localizedHeaders['player_activity_summary.mtt_played'],
        sortable: true,
    };
    const playerActivitySummaryPlayerProfitLoss: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.player_activity_summary?.player_profit_loss, currency}),
        field: 'player_activity_summary.player_profit_loss',
        title: localizedHeaders['player_activity_summary.player_profit_loss'],
        sortable: true,
    };
    const playerActivitySummaryPlayerWinLoss: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.player_activity_summary?.player_win_loss, currency}),
        field: 'player_activity_summary.player_win_loss',
        title: localizedHeaders['player_activity_summary.player_win_loss'],
        sortable: true,
    };
    const playerActivitySummaryNetDeposit: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.player_activity_summary?.net_deposit, currency}),
        field: 'player_activity_summary.net_deposit',
        title: localizedHeaders['player_activity_summary.net_deposit'],
        sortable: true,
    };
    const playerEcologyEcologyFlag: TextColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'text',
        valueGetter: m => m?.player_ecology?.ecology_flag,
        field: 'player_ecology.ecology_flag',
        title: localizedHeaders['player_ecology.ecology_flag'],
        sortable: true,
    };
    const playerEcologyIsProShortdeck: ChipColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'chip',
        valueGetter: m => getIsProColumnValue(m?.player_ecology?.is_pro_shortdeck),
        field: 'player_ecology.is_pro_shortdeck',
        title: localizedHeaders['player_ecology.is_pro_shortdeck'],
        sortable: true,
    };
    const playerEcologyIsProZoom: ChipColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'chip',
        valueGetter: m => getIsProColumnValue(m?.player_ecology?.is_pro_zoom),
        field: 'player_ecology.is_pro_zoom',
        title: localizedHeaders['player_ecology.is_pro_zoom'],
        sortable: true,
    };
    const playerEcologyStake2IsProNlhe: ChipColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'chip',
        valueGetter: m => getIsProColumnValue(m?.player_ecology?.stake_2_is_pro_nlhe),
        field: 'player_ecology.stake_2_is_pro_nlhe',
        title: localizedHeaders['player_ecology.stake_2_is_pro_nlhe'],
        sortable: true,
    };
    const playerEcologyStake4IsProNlhe: ChipColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'chip',
        valueGetter: m => getIsProColumnValue(m?.player_ecology?.stake_4_is_pro_nlhe),
        field: 'player_ecology.stake_4_is_pro_nlhe',
        title: localizedHeaders['player_ecology.stake_4_is_pro_nlhe'],
        sortable: true,
    };
    const playerEcologyStake10IsProNlhe: ChipColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'chip',
        valueGetter: m => getIsProColumnValue(m?.player_ecology?.stake_10_is_pro_nlhe),
        field: 'player_ecology.stake_10_is_pro_nlhe',
        title: localizedHeaders['player_ecology.stake_10_is_pro_nlhe'],
        sortable: true,
    };
    const playerEcologyStake20IsProNlhe: ChipColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'chip',
        valueGetter: m => getIsProColumnValue(m?.player_ecology?.stake_20_is_pro_nlhe),
        field: 'player_ecology.stake_20_is_pro_nlhe',
        title: localizedHeaders['player_ecology.stake_20_is_pro_nlhe'],
        sortable: true,
    };
    const playerEcologyStake50IsProNlhe: ChipColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'chip',
        valueGetter: m => getIsProColumnValue(m?.player_ecology?.stake_50_is_pro_nlhe),
        field: 'player_ecology.stake_50_is_pro_nlhe',
        title: localizedHeaders['player_ecology.stake_50_is_pro_nlhe'],
        sortable: true,
    };
    const playerEcologyStake100IsProNlhe: ChipColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'chip',
        valueGetter: m => getIsProColumnValue(m?.player_ecology?.stake_100_is_pro_nlhe),
        field: 'player_ecology.stake_100_is_pro_nlhe',
        title: localizedHeaders['player_ecology.stake_100_is_pro_nlhe'],
        sortable: true,
    };
    const playerEcologyStake200IsProNlhe: ChipColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'chip',
        valueGetter: m => getIsProColumnValue(m?.player_ecology?.stake_200_is_pro_nlhe),
        field: 'player_ecology.stake_200_is_pro_nlhe',
        title: localizedHeaders['player_ecology.stake_200_is_pro_nlhe'],
        sortable: true,
    };
    const playerEcologyStake400IsProNlhe: ChipColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'chip',
        valueGetter: m => getIsProColumnValue(m?.player_ecology?.stake_400_is_pro_nlhe),
        field: 'player_ecology.stake_400_is_pro_nlhe',
        title: localizedHeaders['player_ecology.stake_400_is_pro_nlhe'],
        sortable: true,
    };
    const pokerMttSummaryMttGgr: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_mtt_summary?.mtt_ggr, currency}),
        field: 'poker_mtt_summary.mtt_ggr',
        title: localizedHeaders['poker_mtt_summary.mtt_ggr'],
        sortable: true,
    };
    const pokerMttSummaryMttNgr: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_mtt_summary?.mtt_ngr, currency}),
        field: 'poker_mtt_summary.mtt_ngr',
        title: localizedHeaders['poker_mtt_summary.mtt_ngr'],
        sortable: true,
    };
    const pokerMttSummaryMttTicketBonus: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_mtt_summary?.mtt_ticket_bonus, currency}),
        field: 'poker_mtt_summary.mtt_ticket_bonus',
        title: localizedHeaders['poker_mtt_summary.mtt_ticket_bonus'],
        sortable: true,
    };
    const pokerMttSummaryMttTotalBuyin: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_mtt_summary?.mtt_total_buyin, currency}),
        field: 'poker_mtt_summary.mtt_total_buyin',
        title: localizedHeaders['poker_mtt_summary.mtt_total_buyin'],
        sortable: true,
    };
    const pokerMttSummaryPlayerMttWinLoss: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_mtt_summary?.player_mtt_win_loss, currency}),
        field: 'poker_mtt_summary.player_mtt_win_loss',
        title: localizedHeaders['poker_mtt_summary.player_mtt_win_loss'],
        sortable: true,
    };
    const pokerMttSummaryPokerMttFees: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_mtt_summary?.poker_mtt_fees, currency}),
        field: 'poker_mtt_summary.poker_mtt_fees',
        title: localizedHeaders['poker_mtt_summary.poker_mtt_fees'],
        sortable: true,
    };
    const pokerNlheSummaryIsPro: ChipColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'chip',
        valueGetter: m => ({
            value:
                m?.poker_nlhe_summary?.is_pro === 0
                    ? formatMessage(localized.nonPro)
                    : m?.poker_nlhe_summary?.is_pro === 1
                      ? formatMessage(localized.pro)
                      : null,
            color: 'grey',
            type: 'round',
        }),
        field: 'poker_nlhe_summary.is_pro',
        title: localizedHeaders['poker_nlhe_summary.is_pro'],
        sortable: true,
    };
    const pokerNlheSummaryIsProShortdeck: ChipColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'chip',
        valueGetter: m => ({
            value:
                m?.poker_nlhe_summary?.is_pro_shortdeck === 0
                    ? formatMessage(localized.nonPro)
                    : m?.poker_nlhe_summary?.is_pro_shortdeck === 1
                      ? formatMessage(localized.pro)
                      : null,
            color: 'grey',
            type: 'round',
        }),
        field: 'poker_nlhe_summary.is_pro_shortdeck',
        title: localizedHeaders['poker_nlhe_summary.is_pro_shortdeck'],
        sortable: true,
    };
    const pokerNlheSummaryNlheAddOnGgrUsdPro: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_nlhe_summary?.nlhe_add_on_ggr_usd_pro, currency}),
        field: 'poker_nlhe_summary.nlhe_add_on_ggr_usd_pro',
        title: localizedHeaders['poker_nlhe_summary.nlhe_add_on_ggr_usd_pro'],
        sortable: true,
    };
    const pokerNlheSummaryNlheGgrUsdPro: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_nlhe_summary?.nlhe_ggr_usd_pro, currency}),
        field: 'poker_nlhe_summary.nlhe_ggr_usd_pro',
        title: localizedHeaders['poker_nlhe_summary.nlhe_ggr_usd_pro'],
        sortable: true,
    };
    const pokerNlheSummaryNlheInsuranceGgrUsdPro: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_nlhe_summary?.nlhe_insurance_ggr_usd_pro, currency}),
        field: 'poker_nlhe_summary.nlhe_insurance_ggr_usd_pro',
        title: localizedHeaders['poker_nlhe_summary.nlhe_insurance_ggr_usd_pro'],
        sortable: true,
    };
    const pokerNlheSummaryNlheJackpotGgrUsdPro: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_nlhe_summary?.nlhe_jackpot_ggr_usd_pro, currency}),
        field: 'poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro',
        title: localizedHeaders['poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro'],
        sortable: true,
    };
    const pokerNlheSummaryNlheRakesGgrUsdPro: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_nlhe_summary?.nlhe_rakes_ggr_usd_pro, currency}),
        field: 'poker_nlhe_summary.nlhe_rakes_ggr_usd_pro',
        title: localizedHeaders['poker_nlhe_summary.nlhe_rakes_ggr_usd_pro'],
        sortable: true,
    };
    const pokerNlheSummaryNlheMicroGgrUsdPro: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_nlhe_summary?.nlhe_micro_ggr_usd_pro, currency}),
        field: 'poker_nlhe_summary.nlhe_micro_ggr_usd_pro',
        title: localizedHeaders['poker_nlhe_summary.nlhe_micro_ggr_usd_pro'],
        sortable: true,
    };
    const pokerNlheSummaryNlheLowGgrUsdPro: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_nlhe_summary?.nlhe_low_ggr_usd_pro, currency}),
        field: 'poker_nlhe_summary.nlhe_low_ggr_usd_pro',
        title: localizedHeaders['poker_nlhe_summary.nlhe_low_ggr_usd_pro'],
        sortable: true,
    };
    const pokerNlheSummaryNlheMediumGgrUsdPro: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_nlhe_summary?.nlhe_medium_ggr_usd_pro, currency}),
        field: 'poker_nlhe_summary.nlhe_medium_ggr_usd_pro',
        title: localizedHeaders['poker_nlhe_summary.nlhe_medium_ggr_usd_pro'],
        sortable: true,
    };
    const pokerNlheSummaryNlheHighGgrUsdPro: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_nlhe_summary?.nlhe_high_ggr_usd_pro, currency}),
        field: 'poker_nlhe_summary.nlhe_high_ggr_usd_pro',
        title: localizedHeaders['poker_nlhe_summary.nlhe_high_ggr_usd_pro'],
        sortable: true,
    };
    const pokerNlheSummaryPlayerNlheShortdeckWinLossLifetime: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => (m.isSummary ? null : {amount: m?.poker_nlhe_summary?.player_nlhe_shortdeck_win_loss_lifetime, currency}),
        field: 'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime',
        title: localizedHeaders['poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime'],
        sortable: true,
    };

    const pokerNlheSummaryGgrPokerPrev180Days: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => (m.isSummary ? null : {amount: m?.poker_nlhe_summary?.ggr_poker_prev_180_days, currency}),
        field: 'poker_nlhe_summary.ggr_poker_prev_180_days',
        title: localizedHeaders['poker_nlhe_summary.ggr_poker_prev_180_days'],
        sortable: true,
    };

    const pokerNlheSummaryPlayerNlheShortdeckWinLossPrev180Days: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => (m.isSummary ? null : {amount: m?.poker_nlhe_summary?.player_nlhe_shortdeck_win_loss_prev_180_days, currency}),
        field: 'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days',
        title: localizedHeaders['poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days'],
        sortable: true,
    };
    const pokerNlheSummaryShortdeckAddOnGgrUsdPro: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_nlhe_summary?.shortdeck_add_on_ggr_usd_pro, currency}),
        field: 'poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro',
        title: localizedHeaders['poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro'],
        sortable: true,
    };
    const pokerNlheSummaryShortdeckGgrUsdPro: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_nlhe_summary?.shortdeck_ggr_usd_pro, currency}),
        field: 'poker_nlhe_summary.shortdeck_ggr_usd_pro',
        title: localizedHeaders['poker_nlhe_summary.shortdeck_ggr_usd_pro'],
        sortable: true,
    };
    const pokerNlheSummaryShortdeckInsuranceGgrUsdPro: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_nlhe_summary?.shortdeck_insurance_ggr_usd_pro, currency}),
        field: 'poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro',
        title: localizedHeaders['poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro'],
        sortable: true,
    };
    const pokerNlheSummaryShortdeckRakesGgrUsdPro: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_nlhe_summary?.shortdeck_rakes_ggr_usd_pro, currency}),
        field: 'poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro',
        title: localizedHeaders['poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro'],
        sortable: true,
    };
    const pokerPloSummaryPloGgr: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_plo_summary?.plo_ggr, currency}),
        field: 'poker_plo_summary.plo_ggr',
        title: localizedHeaders['poker_plo_summary.plo_ggr'],
        sortable: true,
    };
    const pokerSummaryPokerAddons: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_summary?.poker_addons, currency}),
        field: 'poker_summary.poker_addons',
        title: localizedHeaders['poker_summary.poker_addons'],
        sortable: true,
    };
    const pokerSummaryPokerAddonsAddOn: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_summary?.poker_addons_add_on, currency}),
        field: 'poker_summary.poker_addons_add_on',
        title: localizedHeaders['poker_summary.poker_addons_add_on'],
        sortable: true,
    };
    const pokerSummaryPokerAddonsGuess: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_summary?.poker_addons_guess, currency}),
        field: 'poker_summary.poker_addons_guess',
        title: localizedHeaders['poker_summary.poker_addons_guess'],
        sortable: true,
    };
    const pokerSummaryPokerAddonsInsurance: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_summary?.poker_addons_insurance, currency}),
        field: 'poker_summary.poker_addons_insurance',
        title: localizedHeaders['poker_summary.poker_addons_insurance'],
        sortable: true,
    };
    const pokerSummaryPokerGgr: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_summary?.poker_ggr, currency}),
        field: 'poker_summary.poker_ggr',
        title: localizedHeaders['poker_summary.poker_ggr'],
        sortable: true,
    };
    const pokerSummaryPokerGgrUsdProEcologyModified: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_summary?.poker_ggr_usd_pro_ecology_modified, currency}),
        field: 'poker_summary.poker_ggr_usd_pro_ecology_modified',
        title: localizedHeaders['poker_summary.poker_ggr_usd_pro_ecology_modified'],
        sortable: true,
    };
    const pokerSummaryPokerNgr: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_summary?.poker_ngr, currency}),
        field: 'poker_summary.poker_ngr',
        title: localizedHeaders['poker_summary.poker_ngr'],
        sortable: true,
    };
    const pokerSummaryPokerRake: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_summary?.poker_rake, currency}),
        field: 'poker_summary.poker_rake',
        title: localizedHeaders['poker_summary.poker_rake'],
        sortable: true,
    };
    const pokerSummaryPokerShare: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.poker_summary?.poker_share, currency}),
        field: 'poker_summary.poker_share',
        title: localizedHeaders['poker_summary.poker_share'],
        sortable: true,
    };
    const shareSummaryCasinoShare: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.share_summary?.casino_share, currency}),
        field: 'share_summary.casino_share',
        title: localizedHeaders['share_summary.casino_share'],
        sortable: true,
    };
    const shareSummaryGlobalSpinsExpectedGgr: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.share_summary?.global_spins_expected_ggr, currency}),
        field: 'share_summary.global_spins_expected_ggr',
        title: localizedHeaders['share_summary.global_spins_expected_ggr'],
        sortable: true,
    };
    const shareSummaryMttGgr: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.share_summary?.mtt_ggr, currency}),
        field: 'share_summary.mtt_ggr',
        title: localizedHeaders['share_summary.mtt_ggr'],
        sortable: true,
    };
    const shareSummaryPloGgr: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.share_summary?.plo_ggr, currency}),
        field: 'share_summary.plo_ggr',
        title: localizedHeaders['share_summary.plo_ggr'],
        sortable: true,
    };
    const shareSummaryPokerShare: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.share_summary?.poker_share, currency}),
        field: 'share_summary.poker_share',
        title: localizedHeaders['share_summary.poker_share'],
        sortable: true,
    };
    const shareSummarySportsSettledBet: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.share_summary?.sports_settled_bet, currency}),
        field: 'share_summary.sports_settled_bet',
        title: localizedHeaders['share_summary.sports_settled_bet'],
        sortable: true,
    };
    const sportsSummarySportsGgr: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.sports_summary?.sports_ggr, currency}),
        field: 'sports_summary.sports_ggr',
        title: localizedHeaders['sports_summary.sports_ggr'],
        sortable: true,
    };
    const sportsSummarySportsSettledBet: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m?.sports_summary?.sports_settled_bet, currency}),
        field: 'sports_summary.sports_settled_bet',
        title: localizedHeaders['sports_summary.sports_settled_bet'],
        sortable: true,
    };
    const iso_alpha2_country_code: TextColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'text',
        valueGetter: m => m.iso_alpha2_country_code,
        field: 'iso_alpha2_country_code',
        title: localizedHeaders.iso_alpha2_country_code,
        sortable: true,
    };
    const path: TextColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'text',
        valueGetter: m => m.path,
        field: 'path',
        title: localizedHeaders.path,
        sortable: true,
    };
    const referrer_agent_id: TextColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'text',
        valueGetter: m => m.referrer_agent_id,
        field: 'referrer_agent_id',
        title: localizedHeaders.referrer_agent_id,
        sortable: true,
    };
    const referrer_agent_username: TextColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'text',
        valueGetter: m => m.referrer_agent_username,
        field: 'referrer_agent_username',
        title: localizedHeaders.referrer_agent_username,
        sortable: true,
    };
    const uid: TextColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'text',
        valueGetter: m => (m.isSummary ? formatMessage(localized.total) : m.uid),
        field: 'uid',
        title: localizedHeaders.uid,
        sortable: true,
    };
    const username: TextColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'text',
        valueGetter: m => m.username,
        field: 'username',
        title: localizedHeaders.username,
        sortable: true,
    };
    const totalGgrSummaryGgr: CurrencyColumn<PlayerLevelReportV2ViewModel> = {
        ...defaultColumn,
        type: 'currency',
        valueGetter: m => ({amount: m.total_ggr_summary?.ggr, currency}),
        field: 'total_ggr_summary.ggr',
        title: localizedHeaders['total_ggr_summary.ggr'],
        sortable: true,
    };

    function getIsProColumnValue(value: IsPro): ChipValue {
        const formattedValueMap: Record<IsPro, string> = {
            Pro: formatMessage(localized.pro),
            NonPro: formatMessage(localized.nonPro),
            Mixed: formatMessage(localized.mixed),
        };
        return {value: formattedValueMap[value] ?? value, color: 'grey', type: 'round'};
    }

    const config: Record<PlayerLevelReportV2ViewModelKeys, DataGridColumn<PlayerLevelReportV2ViewModel>> = {
        ['bonus_cost_summary.cash_realized_bonus']: bonusCostSummaryCashRealizedBonus,
        ['bonus_cost_summary.cost_ftd_bonus_pro']: bonusCostSummaryCostFtdBonusPro,
        ['bonus_cost_summary.cost_psp_fee_pro']: bonusCostSummaryCostPspFeePro,
        ['bonus_cost_summary.cost_tournament_overlay']: bonusCostSummaryCostTournamentOverlay,
        ['bonus_cost_summary.cost_tournament_overlay_excl_freeroll']: bonusCostSummaryCostTournamentOverlayExclFreeroll,
        ['bonus_cost_summary.mtt_ticket_bonus']: bonusCostSummaryMttTicketBonus,
        ['bonus_cost_summary.poker_bonus']: bonusCostSummaryPokerBonus,
        ['bonus_cost_summary.psp_fee']: bonusCostSummaryPspFee,
        ['casino_summary.casino_bonus']: casinoSummaryCasinoBonus,
        ['casino_summary.casino_ggr']: casinoSummaryCasinoGgr,
        ['casino_summary.casino_ngr']: casinoSummaryCasinoNgr,
        ['casino_summary.casino_share']: casinoSummaryCasinoShare,
        ['casino_summary.external_casino_ggr']: casinoSummaryExternalCasinoGgr,
        ['casino_summary.internal_casino_ggr']: casinoSummaryInternalCasinoGgr,
        ['casino_summary.casino_wager']: casinoSummaryCasinoWager,
        ['global_spins_summary.cost_global_spins_tickets']: globalSpinsSummaryCostGlobalSpinsTickets,
        ['global_spins_summary.global_spins_expected_ggr']: globalSpinsSummaryGlobalSpinsExpectedGgr,
        ['global_spins_summary.global_spins_ggr']: globalSpinsSummaryGlobalSpinsGgr,
        ['player_activity_summary.cash_game_hands_played']: playerActivitySummaryCashGameHandsPlayed,
        ['player_activity_summary.ecology_flag']: playerActivitySummaryEcologyFlag,
        ['player_activity_summary.mtt_played']: playerActivitySummaryMttPlayed,
        ['player_activity_summary.player_profit_loss']: playerActivitySummaryPlayerProfitLoss,
        ['player_activity_summary.player_win_loss']: playerActivitySummaryPlayerWinLoss,
        ['player_activity_summary.net_deposit']: playerActivitySummaryNetDeposit,
        ['player_ecology.ecology_flag']: playerEcologyEcologyFlag,
        ['player_ecology.is_pro_shortdeck']: playerEcologyIsProShortdeck,
        ['player_ecology.is_pro_zoom']: playerEcologyIsProZoom,
        ['player_ecology.stake_2_is_pro_nlhe']: playerEcologyStake2IsProNlhe,
        ['player_ecology.stake_4_is_pro_nlhe']: playerEcologyStake4IsProNlhe,
        ['player_ecology.stake_10_is_pro_nlhe']: playerEcologyStake10IsProNlhe,
        ['player_ecology.stake_20_is_pro_nlhe']: playerEcologyStake20IsProNlhe,
        ['player_ecology.stake_50_is_pro_nlhe']: playerEcologyStake50IsProNlhe,
        ['player_ecology.stake_100_is_pro_nlhe']: playerEcologyStake100IsProNlhe,
        ['player_ecology.stake_200_is_pro_nlhe']: playerEcologyStake200IsProNlhe,
        ['player_ecology.stake_400_is_pro_nlhe']: playerEcologyStake400IsProNlhe,
        ['poker_mtt_summary.mtt_ggr']: pokerMttSummaryMttGgr,
        ['poker_mtt_summary.mtt_ngr']: pokerMttSummaryMttNgr,
        ['poker_mtt_summary.mtt_ticket_bonus']: pokerMttSummaryMttTicketBonus,
        ['poker_mtt_summary.mtt_total_buyin']: pokerMttSummaryMttTotalBuyin,
        ['poker_mtt_summary.player_mtt_win_loss']: pokerMttSummaryPlayerMttWinLoss,
        ['poker_mtt_summary.poker_mtt_fees']: pokerMttSummaryPokerMttFees,
        ['poker_nlhe_summary.is_pro']: pokerNlheSummaryIsPro,
        ['poker_nlhe_summary.is_pro_shortdeck']: pokerNlheSummaryIsProShortdeck,
        ['poker_nlhe_summary.nlhe_add_on_ggr_usd_pro']: pokerNlheSummaryNlheAddOnGgrUsdPro,
        ['poker_nlhe_summary.nlhe_ggr_usd_pro']: pokerNlheSummaryNlheGgrUsdPro,
        ['poker_nlhe_summary.nlhe_insurance_ggr_usd_pro']: pokerNlheSummaryNlheInsuranceGgrUsdPro,
        ['poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro']: pokerNlheSummaryNlheJackpotGgrUsdPro,
        ['poker_nlhe_summary.nlhe_rakes_ggr_usd_pro']: pokerNlheSummaryNlheRakesGgrUsdPro,
        ['poker_nlhe_summary.nlhe_micro_ggr_usd_pro']: pokerNlheSummaryNlheMicroGgrUsdPro,
        ['poker_nlhe_summary.nlhe_low_ggr_usd_pro']: pokerNlheSummaryNlheLowGgrUsdPro,
        ['poker_nlhe_summary.nlhe_medium_ggr_usd_pro']: pokerNlheSummaryNlheMediumGgrUsdPro,
        ['poker_nlhe_summary.nlhe_high_ggr_usd_pro']: pokerNlheSummaryNlheHighGgrUsdPro,
        ['poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime']: pokerNlheSummaryPlayerNlheShortdeckWinLossLifetime,
        ['poker_nlhe_summary.ggr_poker_prev_180_days']: pokerNlheSummaryGgrPokerPrev180Days,
        ['poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days']: pokerNlheSummaryPlayerNlheShortdeckWinLossPrev180Days,
        ['poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro']: pokerNlheSummaryShortdeckAddOnGgrUsdPro,
        ['poker_nlhe_summary.shortdeck_ggr_usd_pro']: pokerNlheSummaryShortdeckGgrUsdPro,
        ['poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro']: pokerNlheSummaryShortdeckInsuranceGgrUsdPro,
        ['poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro']: pokerNlheSummaryShortdeckRakesGgrUsdPro,
        ['poker_plo_summary.plo_ggr']: pokerPloSummaryPloGgr,
        ['poker_summary.poker_addons']: pokerSummaryPokerAddons,
        ['poker_summary.poker_addons_add_on']: pokerSummaryPokerAddonsAddOn,
        ['poker_summary.poker_addons_guess']: pokerSummaryPokerAddonsGuess,
        ['poker_summary.poker_addons_insurance']: pokerSummaryPokerAddonsInsurance,
        ['poker_summary.poker_ggr']: pokerSummaryPokerGgr,
        ['poker_summary.poker_ggr_usd_pro_ecology_modified']: pokerSummaryPokerGgrUsdProEcologyModified,
        ['poker_summary.poker_ngr']: pokerSummaryPokerNgr,
        ['poker_summary.poker_rake']: pokerSummaryPokerRake,
        ['poker_summary.poker_share']: pokerSummaryPokerShare,
        ['share_summary.casino_share']: shareSummaryCasinoShare,
        ['share_summary.global_spins_expected_ggr']: shareSummaryGlobalSpinsExpectedGgr,
        ['share_summary.mtt_ggr']: shareSummaryMttGgr,
        ['share_summary.plo_ggr']: shareSummaryPloGgr,
        ['share_summary.poker_share']: shareSummaryPokerShare,
        ['share_summary.sports_settled_bet']: shareSummarySportsSettledBet,
        ['sports_summary.sports_ggr']: sportsSummarySportsGgr,
        ['sports_summary.sports_settled_bet']: sportsSummarySportsSettledBet,
        iso_alpha2_country_code,
        path,
        referrer_agent_id,
        referrer_agent_username,
        uid,
        username,
        ['total_ggr_summary.ggr']: totalGgrSummaryGgr,
    };

    return useMemo(() => {
        return keys.map(k => config[k]);
    }, [keys.join(',')]);
}

type UsePlayerLevelReportV2SettingsProps = {
    viewType: ViewType;
    defaultColumns: PlayerLevelReportV2ViewModelKeys[];
    pinnedColumns?: PlayerLevelReportV2ViewModelKeys[];
};

type UsePlayerLevelReportV2SettingsResult = {
    visibleColumns?: PlayerLevelReportV2ViewModelKeys[];
    onVisibleColumnsChange: (value: PlayerLevelReportV2ViewModelKeys[]) => void;
};

export function usePlayerLevelReportV2Settings({
    viewType,
    defaultColumns,
    pinnedColumns,
}: UsePlayerLevelReportV2SettingsProps): UsePlayerLevelReportV2SettingsResult {
    const {sub} = useAuthUser();
    const mapper = useAutoMapper();
    const {visible, onVisibleColumnsChange} = useColumnsSettings<PlayerLevelReportV2ColumnSetting>({
        userId: sub,
        settingId: viewType,
        defaultColumns: mapViewModelKeysToSettingsKeys(defaultColumns),
        pinnedColumns: mapViewModelKeysToSettingsKeys(pinnedColumns),
    });

    function mapViewModelKeysToSettingsKeys(keys: PlayerLevelReportV2ViewModelKeys[]): PlayerLevelReportV2ColumnSettingKeys[] {
        return [
            ...new Set(
                keys?.map(k =>
                    mapper.map<PlayerLevelReportV2ViewModelKeys, PlayerLevelReportV2ColumnSettingKeys>(
                        k,
                        PlayerLevelReportV2ViewModelKeysTypeName,
                        PlayerLevelReportV2ColumnSettingKeysTypeName
                    )
                ) ?? []
            ),
        ];
    }

    function mapSettingsKeysToViewModelKeys(keys: PlayerLevelReportV2ColumnSettingKeys[]): PlayerLevelReportV2ViewModelKeys[] {
        return [
            ...new Set(
                keys?.map(k =>
                    mapper.map<PlayerLevelReportV2ColumnSettingKeys, PlayerLevelReportV2ViewModelKeys>(
                        k,
                        PlayerLevelReportV2ColumnSettingKeysTypeName,
                        PlayerLevelReportV2ViewModelKeysTypeName
                    )
                ) ?? []
            ),
        ];
    }

    function handleColumnSettingsChange(keys: PlayerLevelReportV2ViewModelKeys[]): void {
        const columns = mapViewModelKeysToSettingsKeys(keys);
        onVisibleColumnsChange(columns);
    }

    return {
        visibleColumns: mapSettingsKeysToViewModelKeys(visible).filter(f => defaultColumns.includes(f)),
        onVisibleColumnsChange: handleColumnSettingsChange,
    };
}

type UsePlayerLevelReportV2DownloadProps = Pick<
    UseDownloadProps<PlayerLevelReportV2ViewModelKeys>,
    'keys' | 'filename' | 'filter' | 'total' | 'mode'
>;

export function usePlayerLevelReportV2Download(props: UsePlayerLevelReportV2DownloadProps): UseDownloadResult {
    const {handleDownload, isProgress} = useDownload<PlayerLevelReportV2ViewModelKeys>({
        ...props,
        strategySymbol: props.mode === 'client' ? ServiceTypes.DownloadClientStrategy : ServiceTypes.DownloadServerStrategy,
        headersLocalization: localized,
    });

    return {handleDownload, isProgress};
}

const rowColumnsResource = defineAccessControlledResource({
    name: 'PlayerLevelReport_DataGrid_RowColumns',
    rules: new GroupAccessRule([groups.aalRowGroup, groups.aalRowManagersGroup, groups.adminsGroup]),
});
